import { useState, useEffect, useRef, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/plugins/regions';
import TimelinePlugin from 'wavesurfer.js/plugins/timeline';
import Sidebar from './Sidebar';
import { getDailyNext, submitDailyAnnotation } from '../services/api';

// ── Tag config ────────────────────────────────────────────────────────────────
const TAGS = ['adult', 'kid', 'noise', 'doro'];

const TAG_COLORS = {
  adult: 'rgba(59,130,246,0.55)',
  kid:   'rgba(34,197,94,0.55)',
  noise: 'rgba(150,150,150,0.45)',
  doro:  'rgba(168,85,247,0.55)',
};

const TAG_SOLID = {
  adult: '#3b82f6',
  kid:   '#22c55e',
  noise: '#9ca3af',
  doro:  '#a855f7',
};

function regionColor(tags) {
  if (!tags || tags.length === 0) return 'rgba(150,150,150,0.3)';
  if (tags.length === 1) return TAG_COLORS[tags[0]] ?? 'rgba(150,150,150,0.3)';
  const stops = tags.flatMap((t, i) => {
    const p1 = ((i / tags.length) * 100).toFixed(1);
    const p2 = (((i + 1) / tags.length) * 100).toFixed(1);
    return [`${TAG_COLORS[t]} ${p1}%`, `${TAG_COLORS[t]} ${p2}%`];
  });
  return `repeating-linear-gradient(45deg, ${stops.join(', ')})`;
}

function fmt(sec) {
  if (!isFinite(sec)) return '0:00.0';
  const m = Math.floor(sec / 60);
  const s = (sec % 60).toFixed(1).padStart(4, '0');
  return `${m}:${s}`;
}

export default function DailyAnnotation({ user, onPageChange }) {
  // ── Refs ──────────────────────────────────────────────────────────────────
  const containerRef     = useRef(null);
  const wsRef            = useRef(null);
  const regionsPluginRef = useRef(null);
  const regionMapRef     = useRef({});
  const durationRef      = useRef(0);
  const rafRef           = useRef(null);
  const sliceRef         = useRef(null);

  // ── State ─────────────────────────────────────────────────────────────────
  const [item, setItem]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [audioLoading, setAudioLoading] = useState(false);
  const [done, setDone]               = useState(false);
  const [playing, setPlaying]         = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]       = useState(0);
  const [zoom, setZoom]               = useState(50);
  const [rate, setRate]               = useState(1);
  const [regions, setRegions]         = useState([]);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState(null);

  // ── WaveSurfer init (once) ─────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    let destroyed = false;

    const regionsPlugin = RegionsPlugin.create();
    const timeline = TimelinePlugin.create({
      height: 18, timeInterval: 1, primaryLabelInterval: 5,
    });

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#b3f5d8',
      progressColor: '#3a8f68',
      cursorColor: '#ffffff',
      cursorWidth: 1,
      height: 130,
      minPxPerSec: zoom,
      scrollParent: true,
      normalize: true,
      plugins: [regionsPlugin, timeline],
    });

    wsRef.current = ws;
    regionsPluginRef.current = regionsPlugin;

    ws.on('ready', () => {
      if (destroyed) return;
      const d = ws.getDuration();
      setDuration(d);
      durationRef.current = d;
      setAudioLoading(false);
      createInitialRegion(regionsPlugin, d);
    });

    ws.on('audioprocess', t => {
      if (destroyed || rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (!destroyed) setCurrentTime(t);
      });
    });
    ws.on('seeking', t  => { if (!destroyed) setCurrentTime(t); });
    ws.on('play',    ()  => { if (!destroyed) setPlaying(true);  });
    ws.on('pause',   ()  => { if (!destroyed) setPlaying(false); });
    ws.on('finish',  ()  => { if (!destroyed) setPlaying(false); });

    regionsPlugin.on('region-updated', r => {
      if (destroyed) return;
      const existing = regionMapRef.current[r.id];
      if (existing) {
        regionMapRef.current[r.id] = { ...existing, start: r.start, end: r.end };
      }
    });

    regionsPlugin.on('region-update-end', r => {
      if (destroyed) return;
      const existing = regionMapRef.current[r.id];
      if (!existing) return;
      regionMapRef.current[r.id] = { ...existing, start: r.start, end: r.end };
      const sorted = Object.values(regionMapRef.current).sort((a, b) => a.start - b.start);
      const idx = sorted.findIndex(reg => reg.id === r.id);
      if (idx >= 0 && idx < sorted.length - 1) {
        const rightData = sorted[idx + 1];
        const wsRight = regionsPlugin.getRegions().find(wr => wr.id === rightData.id);
        if (wsRight) {
          wsRight.setOptions({ start: r.end });
          regionMapRef.current[rightData.id] = { ...rightData, start: r.end };
        }
      }
      if (idx > 0) {
        const leftData = sorted[idx - 1];
        const wsLeft = regionsPlugin.getRegions().find(wr => wr.id === leftData.id);
        if (wsLeft) {
          wsLeft.setOptions({ end: r.start });
          regionMapRef.current[leftData.id] = { ...leftData, end: r.start };
        }
      }
      syncRegions();
    });

    function handleContainerClick() {
      const t = wsRef.current?.getCurrentTime();
      if (t !== undefined) sliceRef.current?.(t);
    }
    containerRef.current.addEventListener('click', handleContainerClick);

    return () => {
      destroyed = true;
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      containerRef.current?.removeEventListener('click', handleContainerClick);
      ws.destroy();
      wsRef.current = null;
      regionsPluginRef.current = null;
      regionMapRef.current = {};
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (duration > 0) wsRef.current?.zoom(zoom); }, [zoom, duration]);
  useEffect(() => { wsRef.current?.setPlaybackRate(rate); }, [rate]);

  // ── Keyboard shortcuts ─────────────────────────────────────────────────
  useEffect(() => {
    function onKey(e) {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.code === 'Space')           { e.preventDefault(); wsRef.current?.playPause(); }
      else if (e.code === 'ArrowLeft')  { e.preventDefault(); wsRef.current?.skip(-2); }
      else if (e.code === 'ArrowRight') { e.preventDefault(); wsRef.current?.skip(2); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Region helpers ─────────────────────────────────────────────────────
  function syncRegions() {
    const sorted = Object.values(regionMapRef.current)
      .sort((a, b) => a.start - b.start);
    setRegions([...sorted]);
  }

  function createInitialRegion(plugin, dur) {
    regionMapRef.current = {};
    plugin.clearRegions();
    const r = plugin.addRegion({
      start: 0,
      end: dur,
      color: regionColor(['noise']),
      drag: false,
      resize: true,
    });
    regionMapRef.current[r.id] = { id: r.id, start: 0, end: dur, tags: ['noise'] };
    syncRegions();
  }

  function sliceRegionAt(t) {
    const plugin = regionsPluginRef.current;
    if (!plugin) return;
    const wsRegions = plugin.getRegions();
    const containing = wsRegions.find(r => r.start <= t && r.end >= t);
    if (!containing) return;
    const data = regionMapRef.current[containing.id];
    if (!data) return;
    const { start, end, tags } = data;
    if (t - start < 0.001 || end - t < 0.001) return;
    containing.remove();
    delete regionMapRef.current[containing.id];
    const left = plugin.addRegion({ start, end: t, color: regionColor(tags), drag: false, resize: true });
    regionMapRef.current[left.id] = { id: left.id, start, end: t, tags: [...tags] };
    const right = plugin.addRegion({ start: t, end, color: regionColor(tags), drag: false, resize: true });
    regionMapRef.current[right.id] = { id: right.id, start: t, end, tags: [...tags] };
    syncRegions();
  }
  sliceRef.current = sliceRegionAt;

  function setRegionTags(id, tags) {
    const existing = regionMapRef.current[id];
    if (!existing) return;
    regionMapRef.current[id] = { ...existing, tags };
    const wsRegion = regionsPluginRef.current?.getRegions().find(r => r.id === id);
    wsRegion?.setOptions({ color: regionColor(tags) });
    syncRegions();
  }

  function mergeWithNext(id) {
    const sorted = Object.values(regionMapRef.current).sort((a, b) => a.start - b.start);
    const idx = sorted.findIndex(r => r.id === id);
    if (idx < 0 || idx >= sorted.length - 1) return;
    const left  = sorted[idx];
    const right = sorted[idx + 1];
    const mergedTags = [...new Set([...left.tags, ...right.tags])];
    regionsPluginRef.current?.getRegions()
      .filter(r => r.id === left.id || r.id === right.id)
      .forEach(r => r.remove());
    delete regionMapRef.current[left.id];
    delete regionMapRef.current[right.id];
    const merged = regionsPluginRef.current?.addRegion({
      start: left.start, end: right.end,
      color: regionColor(mergedTags),
      drag: false, resize: true,
    });
    if (merged) {
      regionMapRef.current[merged.id] = { id: merged.id, start: left.start, end: right.end, tags: mergedTags };
    }
    syncRegions();
  }

  // ── Load next ──────────────────────────────────────────────────────────
  const loadNext = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRegions([]);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    durationRef.current = 0;
    regionMapRef.current = {};
    regionsPluginRef.current?.clearRegions();

    try {
      const next = await getDailyNext();
      if (!next) { setDone(true); setLoading(false); return; }
      setItem(next);
      setAudioLoading(true);
      setLoading(false);
      wsRef.current?.empty();
      wsRef.current?.load(next.audio_proxy_url);
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadNext(); }, [loadNext]);

  // ── Submit / Skip ──────────────────────────────────────────────────────
  async function handleSubmit() {
    if (!item) return;
    const untagged = regions.filter(r => !r.tags || r.tags.length === 0);
    if (untagged.length > 0) {
      setError(`${untagged.length} region(s) have no tags.`);
      return;
    }
    const covered = regions.reduce((acc, r) => acc + (r.end - r.start), 0);
    const dur = durationRef.current;
    if (Math.abs(covered - dur) > 0.001) {
      setError(`Coverage ${covered.toFixed(3)}s ≠ duration ${dur.toFixed(3)}s. Please reload.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const segments = regions.map(r => ({
        start: parseFloat(r.start.toFixed(6)),
        end:   parseFloat(r.end.toFixed(6)),
        tags:  r.tags,
      }));
      await submitDailyAnnotation(item._id, segments);
      loadNext();
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
    } finally {
      setSubmitting(false);
    }
  }

  function handleSkip() { loadNext(); }

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="workspace">
      <Sidebar
        user={user}
        currentPage="daily-annotation"
        onPageChange={onPageChange}
        title={user?.name || user?.email || 'Annotator'}
        subtitle="Daily Annotation"
      />
      <main className="annotator" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="annotator-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <h1 style={{ margin: 0 }}>Daily Annotation</h1>
        </div>

        {done ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <div style={{ fontSize: 48 }}>📭</div>
            <h2>Queue empty</h2>
            <p style={{ color: 'var(--text-muted,#888)' }}>No recordings left to annotate.</p>
            <button className="skip-btn" style={{ width: 'auto', padding: '8px 20px' }}
              onClick={() => { setDone(false); loadNext(); }}>
              Check again
            </button>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* File bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <span style={{ fontSize: 16 }}>🔊</span>
              <span style={{ fontWeight: 600, fontSize: 14 }}>
                {item ? `${item.user_id} / ${item.session_id}` : 'Loading…'}
              </span>
              {duration > 0 && (
                <span className="badge" style={{ background: 'transparent', border: '1px solid var(--border)', marginLeft: 'auto' }}>
                  {fmt(duration)}
                </span>
              )}
            </div>

            {/* Waveform */}
            <div style={{ position: 'relative', background: '#12161a', borderBottom: '1px solid rgba(255,255,255,0.08)', minHeight: 160, flexShrink: 0 }}>
              {audioLoading && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'rgba(10,13,16,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#9ca3af', fontSize: 13 }}>
                  <div style={{ width: 24, height: 24, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'da-spin 0.7s linear infinite' }} />
                  Loading audio…
                </div>
              )}
              <div ref={containerRef} style={{ padding: '8px 0' }} />
            </div>

            {/* Transport */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <TBtn onClick={() => wsRef.current?.seekTo(0)}>⏮</TBtn>
                <TBtn accent onClick={() => wsRef.current?.playPause()}>{playing ? '⏸' : '▶'}</TBtn>
                <TBtn onClick={() => { wsRef.current?.pause(); wsRef.current?.seekTo(0); }}>⏹</TBtn>
                <span style={{ fontSize: 12, color: 'var(--text-muted,#888)', fontVariantNumeric: 'tabular-nums', minWidth: 110 }}>
                  {fmt(currentTime)} / {fmt(duration)}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted,#888)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Zoom</span>
                <input type="range" min="20" max="500" step="10" value={zoom}
                  onChange={e => setZoom(Number(e.target.value))}
                  style={{ width: 120, accentColor: 'var(--accent)' }} />
                <span style={{ fontSize: 11, color: 'var(--text-muted,#888)', minWidth: 48 }}>{zoom}px/s</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted,#888)', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 4 }}>Speed</span>
                {[0.5, 0.75, 1, 1.5, 2].map(r => (
                  <button key={r} onClick={() => setRate(r)} style={{
                    background: rate === r ? 'var(--accent)' : 'transparent',
                    border: `1px solid ${rate === r ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 4, color: 'var(--text)', fontSize: 11, padding: '2px 7px',
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}>{r}×</button>
                ))}
              </div>
            </div>

            {/* Shortcuts */}
            <div style={{ padding: '5px 16px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted,#888)', display: 'flex', gap: 18, flexWrap: 'wrap', flexShrink: 0 }}>
              {[['Space','Play/Pause'],['Click waveform','Slice here'],['Drag edge','Move boundary'],['←/→','Seek ±2s']].map(([k,v]) => (
                <span key={k}><strong style={{ color: 'var(--text)' }}>{k}</strong> {v}</span>
              ))}
            </div>

            {/* Region list + action sidebar */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                {regions.length === 0 ? (
                  <div style={{ padding: '32px 24px', color: 'var(--text-muted,#888)', textAlign: 'center' }}>
                    <p style={{ fontSize: 24, marginBottom: 8 }}>🎙</p>
                    <p>Loading audio…</p>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {['#', 'Start', 'End', 'Duration', 'Tags', ''].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '4px 10px', color: 'var(--text-muted,#888)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {regions.map((r, i) => (
                        <tr key={r.id} style={{ borderLeft: `3px solid ${TAG_SOLID[r.tags[0]] ?? '#9ca3af'}` }}>
                          <td style={{ padding: '7px 10px', color: 'var(--text-muted,#888)' }}>#{i + 1}</td>
                          <td style={{ padding: '7px 10px', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.start)}</td>
                          <td style={{ padding: '7px 10px', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.end)}</td>
                          <td style={{ padding: '7px 10px', color: 'var(--text-muted,#888)' }}>{(r.end - r.start).toFixed(3)}s</td>
                          <td style={{ padding: '6px 10px' }}>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {TAGS.map(tag => {
                                const active = r.tags.includes(tag);
                                return (
                                  <button
                                    key={tag}
                                    onClick={() => {
                                      const next = active
                                        ? r.tags.filter(t => t !== tag)
                                        : [...r.tags, tag];
                                      if (next.length > 0) setRegionTags(r.id, next);
                                    }}
                                    style={{
                                      background: active ? TAG_SOLID[tag] : 'transparent',
                                      border: `1px solid ${TAG_SOLID[tag]}`,
                                      borderRadius: 4,
                                      color: active ? '#fff' : TAG_SOLID[tag],
                                      fontSize: 11, padding: '2px 8px',
                                      cursor: 'pointer', fontFamily: 'inherit',
                                      fontWeight: active ? 600 : 400,
                                    }}
                                  >
                                    {tag}
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                          <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                            {i < regions.length - 1 && (
                              <button
                                onClick={() => mergeWithNext(r.id)}
                                title="Merge with next region"
                                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-muted,#888)', fontSize: 11, padding: '2px 6px', cursor: 'pointer', fontFamily: 'inherit', marginRight: 4 }}
                              >
                                ⊃⊂
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Action sidebar */}
              <div style={{ width: 200, borderLeft: '1px solid var(--border)', padding: 16, display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
                <div style={{ flex: 1 }} />
                {error && <p style={{ color: '#f87171', fontSize: 12, margin: 0 }}>⚠ {error}</p>}
                <button className="skip-btn" onClick={handleSkip} disabled={loading || submitting || audioLoading}>
                  Skip
                </button>
                <button
                  className="save-btn"
                  onClick={handleSubmit}
                  disabled={
                    !item || loading || submitting || audioLoading ||
                    regions.some(r => !r.tags || r.tags.length === 0)
                  }
                >
                  {submitting ? 'Saving…' : 'Submit →'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <style>{`@keyframes da-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function TBtn({ children, onClick, accent }) {
  return (
    <button onClick={onClick} style={{
      background: accent ? 'var(--accent)' : 'transparent',
      border: `1px solid ${accent ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 6, color: accent ? '#fff' : 'var(--text)',
      fontSize: accent ? 14 : 16, width: 34, height: 30,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer',
    }}>{children}</button>
  );
}
